import { getReport, listReportStrategyPolicyVersions } from "../../../services/reportsApi";
import { getBacktestSlim } from "../../../services/backtestsApi";
import type { Report, ReportStrategyPolicyVersion } from "../../../types/reports";
import {
  isPolicyPayloadV1,
  isPolicyTreeBundleV1,
  isPolicyTreeV2,
  type PolicyTreeV2,
  type TradePolicySimulatePolicy,
} from "../../../types/tradePolicy";
import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import {
  collectPolicyCandleTimeframes,
  normalizeCandleTimeframe,
  resolveFinestCandleTimeframe,
  type CandleTimeframeId,
} from "../../../utils/candleTimeframeBarBudget";
import {
  prepareSimulatePolicyWithDualTimeframes,
  resolveDualCandleTimeframes,
} from "../../../utils/dualCandleTimeframe";
import { hydratePolicyTreeManagementSegments } from "../ExecutionRulesSimulator/policyTreeUtils";
import { findUpstreamStrategyEntityId } from "./workspaceGraphUtils";
import {
  findParentCombinedPolicyNode,
  resolveWorkspacePolicyLineage,
} from "./workspaceCombinedPolicyUtils";

export type WorkspacePolicyRunPrefill = {
  /** Canvas node the new sim registers under (temporary for derived Edit & run). */
  attachParent: WorkspaceNode;
  /**
   * When Edit & run from a policy/combined_policy: the source node that should
   * parent the derived policy version after Generate.
   */
  sourcePolicyNode?: WorkspaceNode;
  /** Result node type after save. */
  focusNodeType: "simulation" | "combined_simulation";
  scopedReportId: string;
  strategyId: string;
  /** Report / cohort symbol when set. */
  prefillSymbol: string | null;
  /** V1 / bundle trees for the classic editor. */
  prefillPolicyTree: unknown | null;
  /** V2 tree for trade-management mode. */
  prefillPolicy: PolicyTreeV2 | null;
  policyMode: "v1" | "v2";
  prefillWindow: {
    start_date?: string | null;
    end_date?: string | null;
    timeframe?: string | null;
  } | null;
  /** Locked candle TF for Run (finest of sources / segments / report). */
  candleTimeframe: CandleTimeframeId;
  candleTimeframeLocked: true;
  /** Dual-TF: entry side when RA+EP. */
  entryCandleTimeframe?: CandleTimeframeId;
  /** Dual-TF: Exit Placement / management replay TF. */
  managementCandleTimeframe?: CandleTimeframeId;
  report: Report;
};

function ymdFromReportDate(raw: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw ?? "").trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function prefillWindowFromReport(report: Report): WorkspacePolicyRunPrefill["prefillWindow"] {
  const start =
    ymdFromReportDate(report.cohort_period?.local_start_date) ??
    ymdFromReportDate(report.start_date);
  const end =
    ymdFromReportDate(report.cohort_period?.local_end_date) ??
    ymdFromReportDate(report.end_date);
  return {
    start_date: start,
    end_date: end,
    timeframe: report.timeframe?.trim() || null,
  };
}

function assertSupportedPolicyTree(policy: TradePolicySimulatePolicy): void {
  if (isPolicyTreeV2(policy) || isPolicyPayloadV1(policy) || isPolicyTreeBundleV1(policy)) {
    return;
  }
  throw new Error("Unsupported policy tree shape for Run.");
}

/** Map canvas policy nodes → candle TF for hydrating unstamped management segments. */
export function candleTimeframeByVersionIdFromCanvas(
  nodes: WorkspaceNode[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const n of nodes) {
    if (n.type !== "policy" && n.type !== "combined_policy") continue;
    const id = n.entityId?.trim();
    if (!id) continue;
    const tf =
      n.meta?.managementCandleTimeframe?.trim() ||
      n.meta?.candleTimeframe?.trim() ||
      n.meta?.candleTimeframes?.[0]?.trim() ||
      "";
    if (tf) out[id] = tf;
  }
  return out;
}

/**
 * Resolve locked candle TF for Run:
 * 1) node meta `candleTimeframe` / finest of `candleTimeframes` / legacy `resolvedCandleTimeframe`
 * 2) finest segment TF on the policy tree
 * 3) report `candle_timeframe` (default 1h)
 */
export function resolvePolicyRunCandleTimeframe(input: {
  node: WorkspaceNode;
  policy: unknown;
  report: Report;
}): CandleTimeframeId {
  const fromMeta =
    input.node.meta?.candleTimeframe?.trim() ||
    input.node.meta?.candleTimeframes?.[0]?.trim() ||
    input.node.meta?.resolvedCandleTimeframe?.trim();
  if (fromMeta) {
    return normalizeCandleTimeframe(fromMeta);
  }
  const fromSegments = collectPolicyCandleTimeframes(input.policy);
  if (fromSegments.length > 0) {
    return resolveFinestCandleTimeframe(fromSegments);
  }
  return normalizeCandleTimeframe(input.report.candle_timeframe);
}

/**
 * Load strategy policy version + report window for Run from a policy or
 * combined_policy canvas node. Plain policy attaches under its upstream report;
 * combined_policy attaches under itself (combined_simulation).
 */
export async function loadWorkspacePolicyRunPrefill(
  node: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): Promise<WorkspacePolicyRunPrefill> {
  if (node.type !== "policy" && node.type !== "combined_policy") {
    throw new Error("Run is only available on policy and combined policy nodes.");
  }

  let strategyId: string | null = null;
  let reportId: string | null = null;
  let attachParent: WorkspaceNode;
  let focusNodeType: "simulation" | "combined_simulation";

  if (node.type === "combined_policy") {
    reportId = node.meta?.adoptedReportId?.trim() || null;
    strategyId = findUpstreamStrategyEntityId(node.id, nodes, edges);
    if (!reportId) {
      throw new Error(
        "This combined policy has no adopted report window. Recreate it with a first-selected source policy that has a report."
      );
    }
    attachParent = node;
    focusNodeType = "combined_simulation";
  } else {
    const lineage = resolveWorkspacePolicyLineage(node, nodes, edges);
    strategyId =
      lineage.strategyNode?.entityId?.trim() ||
      findUpstreamStrategyEntityId(node.id, nodes, edges);
    reportId = lineage.reportNode?.entityId?.trim() || null;
    if (!lineage.reportNode) {
      throw new Error(
        "Could not find the upstream report for this policy. Link it under a report → simulation path first."
      );
    }
    // simulation may parent under report (canonical) or policy (Edit & run).
    // Register under the report first; lineage step reparents onto the source
    // policy (unchanged tree) or a derived policy (edited tree).
    attachParent = lineage.reportNode;
    focusNodeType = "simulation";
  }

  if (!strategyId) {
    throw new Error("Could not resolve the strategy for this policy.");
  }
  if (!reportId) {
    throw new Error("Could not resolve the report window for this run.");
  }

  const [versionsResponse, report] = await Promise.all([
    listReportStrategyPolicyVersions(strategyId, { include_archived: true }),
    getReport(reportId),
  ]);

  const match =
    (versionsResponse.versions ?? []).find(
      (row) => String(row.id) === String(node.entityId)
    ) ?? null;
  if (!match?.policy_tree) {
    throw new Error("Policy version not found or has no policy tree.");
  }

  const policyRaw = match.policy_tree as TradePolicySimulatePolicy;
  assertSupportedPolicyTree(policyRaw);

  const versions = versionsResponse.versions ?? [];
  const managementHint =
    node.meta?.managementCandleTimeframe?.trim() || null;
  const hydrated = hydratePolicyTreeManagementSegments(policyRaw, versions, {
    candleTimeframeByVersionId: candleTimeframeByVersionIdFromCanvas(nodes),
    managementCandleTimeframeHint: managementHint,
  }) as TradePolicySimulatePolicy;
  assertSupportedPolicyTree(hydrated);

  const candleTimeframe = resolvePolicyRunCandleTimeframe({
    node,
    policy: hydrated,
    report,
  });
  const dual = resolveDualCandleTimeframes(hydrated, {
    preferredCandleTimeframe: candleTimeframe,
    entryCandleTimeframe: node.meta?.entryCandleTimeframe,
    managementCandleTimeframe:
      managementHint || node.meta?.managementCandleTimeframe,
  });
  const prepared = prepareSimulatePolicyWithDualTimeframes(hydrated, {
    preferredCandleTimeframe: candleTimeframe,
    candleTimeframeLocked: true,
    entryCandleTimeframe: dual.entry_candle_timeframe,
    managementCandleTimeframe: dual.management_candle_timeframe,
  });
  const policy = prepared.policy as TradePolicySimulatePolicy;
  assertSupportedPolicyTree(policy);

  const isV2 = isPolicyTreeV2(policy);

  return {
    attachParent,
    sourcePolicyNode: node,
    focusNodeType,
    scopedReportId: reportId,
    strategyId,
    prefillSymbol: report.symbol?.trim() || null,
    prefillPolicyTree: isV2 ? null : policy,
    prefillPolicy: isV2 ? policy : null,
    policyMode: isV2 ? "v2" : "v1",
    prefillWindow: prefillWindowFromReport(report),
    candleTimeframe,
    candleTimeframeLocked: true,
    ...(dual.entry_candle_timeframe
      ? { entryCandleTimeframe: dual.entry_candle_timeframe }
      : {}),
    ...(dual.management_candle_timeframe
      ? { managementCandleTimeframe: dual.management_candle_timeframe }
      : {}),
    report,
  };
}

function findUpstreamNodeOfType(
  nodeId: string,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  type: WorkspaceNode["type"]
): WorkspaceNode | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const parentsByTarget = new Map<string, string[]>();
  for (const edge of edges) {
    const list = parentsByTarget.get(edge.targetNodeId) ?? [];
    list.push(edge.sourceNodeId);
    parentsByTarget.set(edge.targetNodeId, list);
  }
  const queue = [nodeId];
  const seen = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = byId.get(id);
    if (node?.type === type) return node;
    for (const parentId of parentsByTarget.get(id) ?? []) {
      queue.push(parentId);
    }
  }
  return null;
}

function buildRunPrefillFromPolicyTree(input: {
  policyRaw: TradePolicySimulatePolicy;
  attachParent: WorkspaceNode;
  focusNodeType: "simulation" | "combined_simulation";
  strategyId: string;
  reportId: string;
  report: Report;
  candleHintNode?: WorkspaceNode | null;
  nodes: WorkspaceNode[];
  versions?: ReportStrategyPolicyVersion[];
}): WorkspacePolicyRunPrefill {
  assertSupportedPolicyTree(input.policyRaw);
  const managementHint =
    input.candleHintNode?.meta?.managementCandleTimeframe?.trim() || null;
  const hydrated = hydratePolicyTreeManagementSegments(
    input.policyRaw,
    input.versions ?? [],
    {
      candleTimeframeByVersionId: candleTimeframeByVersionIdFromCanvas(input.nodes),
      managementCandleTimeframeHint: managementHint,
    }
  ) as TradePolicySimulatePolicy;
  assertSupportedPolicyTree(hydrated);

  const candleHint = input.candleHintNode ?? input.attachParent;
  const candleTimeframe = resolvePolicyRunCandleTimeframe({
    node: candleHint,
    policy: hydrated,
    report: input.report,
  });
  const dual = resolveDualCandleTimeframes(hydrated, {
    preferredCandleTimeframe: candleTimeframe,
    entryCandleTimeframe: candleHint.meta?.entryCandleTimeframe,
    managementCandleTimeframe:
      managementHint || candleHint.meta?.managementCandleTimeframe,
  });
  const prepared = prepareSimulatePolicyWithDualTimeframes(hydrated, {
    preferredCandleTimeframe: candleTimeframe,
    candleTimeframeLocked: true,
    entryCandleTimeframe: dual.entry_candle_timeframe,
    managementCandleTimeframe: dual.management_candle_timeframe,
  });
  const policy = prepared.policy as TradePolicySimulatePolicy;
  assertSupportedPolicyTree(policy);
  const isV2 = isPolicyTreeV2(policy);

  return {
    attachParent: input.attachParent,
    focusNodeType: input.focusNodeType,
    scopedReportId: input.reportId,
    strategyId: input.strategyId,
    prefillSymbol: input.report.symbol?.trim() || null,
    prefillPolicyTree: isV2 ? null : policy,
    prefillPolicy: isV2 ? policy : null,
    policyMode: isV2 ? "v2" : "v1",
    prefillWindow: prefillWindowFromReport(input.report),
    candleTimeframe,
    candleTimeframeLocked: true,
    ...(dual.entry_candle_timeframe
      ? { entryCandleTimeframe: dual.entry_candle_timeframe }
      : {}),
    ...(dual.management_candle_timeframe
      ? { managementCandleTimeframe: dual.management_candle_timeframe }
      : {}),
    report: input.report,
  };
}

/**
 * Load a simulation / combined_simulation backtest policy into the editable
 * simulator. New run attaches as a sibling under the upstream report (plain sim)
 * or under the parent combined_policy (combined sim).
 */
export async function loadWorkspaceSimulationEditPrefill(
  node: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): Promise<WorkspacePolicyRunPrefill> {
  if (node.type !== "simulation" && node.type !== "combined_simulation") {
    throw new Error("Edit in simulator is only available on simulation nodes.");
  }
  const backtestId = node.entityId?.trim();
  if (!backtestId) {
    throw new Error("This simulation has no saved backtest id yet.");
  }

  const backtest = await getBacktestSlim(backtestId);
  if (!backtest.policy_tree) {
    throw new Error("This simulation has no policy tree to load.");
  }

  const strategyId =
    backtest.strategy_id?.trim() ||
    findUpstreamStrategyEntityId(node.id, nodes, edges);
  if (!strategyId) {
    throw new Error("Could not resolve the strategy for this simulation.");
  }

  let attachParent: WorkspaceNode;
  let focusNodeType: "simulation" | "combined_simulation";
  let reportId: string | null =
    backtest.report_id?.trim() ||
    node.meta?.adoptedReportId?.trim() ||
    null;

  if (node.type === "combined_simulation") {
    const combinedParent = findParentCombinedPolicyNode(node, nodes, edges);
    if (combinedParent) {
      attachParent = combinedParent;
      focusNodeType = "combined_simulation";
      reportId =
        reportId ||
        combinedParent.meta?.adoptedReportId?.trim() ||
        null;
    } else {
      const reportNode = findUpstreamNodeOfType(node.id, nodes, edges, "report");
      if (!reportNode) {
        throw new Error(
          "Could not find the upstream report or combined policy for this simulation."
        );
      }
      attachParent = reportNode;
      focusNodeType = "simulation";
      reportId = reportId || reportNode.entityId.trim() || null;
    }
  } else {
    const reportNode = findUpstreamNodeOfType(node.id, nodes, edges, "report");
    if (!reportNode) {
      throw new Error(
        "Could not find the upstream report for this simulation. Link it under a report first."
      );
    }
    attachParent = reportNode;
    focusNodeType = "simulation";
    reportId = reportId || reportNode.entityId.trim() || null;
  }

  if (!reportId) {
    throw new Error("Could not resolve the report window for this run.");
  }

  const report = await getReport(reportId);
  let versions: ReportStrategyPolicyVersion[] = [];
  try {
    const versionsResponse = await listReportStrategyPolicyVersions(strategyId, {
      include_archived: true,
    });
    versions = versionsResponse.versions ?? [];
  } catch {
    versions = [];
  }

  return buildRunPrefillFromPolicyTree({
    policyRaw: backtest.policy_tree as TradePolicySimulatePolicy,
    attachParent,
    focusNodeType,
    strategyId,
    reportId,
    report,
    candleHintNode: node,
    nodes,
    versions,
  });
}

